import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../api";
import { useApp } from "../context/AppContext";
import { MODULES, ORDER_STATUS, ORDER_TYPE, ORIGINES, clientName, fmtCHF, fmtDate, orderSummary } from "../utils";
import ArticleAdder, { ArticleRows } from "../components/ArticleAdder";
import ClientCombobox from "../components/ClientCombobox";
import { toast } from "sonner";
import { Plus, CheckCircle2, FileText, Trash2, Pencil, Eye, ReceiptText, Archive } from "lucide-react";

const EMPTY_DETAILS = { articles: [], hours: "", hourly_rate: "", forfait_price: "", mission_type: "", price_override: "" };
const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

export default function Orders() {
  const { module, user } = useApp();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archiveInfo, setArchiveInfo] = useState(null);
  const [prices, setPrices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [refDetail, setRefDetail] = useState(null);
  const [refForm, setRefForm] = useState({ client_id: "", label: "", date: "", amount_ttc: "", margin_percent: "0", receipt: null });
  const [realizeOrder, setRealizeOrder] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);

  const [form, setForm] = useState({ client_id: "", module: "conciergerie", employee_id: "", scheduled_date: "", description: "", notes: "", supplement_produits: false, origine: "saphir_immo", details: { ...EMPTY_DETAILS } });
  const [realize, setRealize] = useState({ started_at: "", ended_at: "", materials: [], receipts: [], notes: "", articles: [] });
  const [mat, setMat] = useState({ name: "", price: "" });
  const [invForm, setInvForm] = useState({ discount_percent: 0, payment_method: "", origine: "", line_discount: 0 });

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  const load = () => {
    const params = {};
    if (module !== "tous") params.module = module;
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.type = typeFilter;
    if (clientFilter) params.client_id = clientFilter;
    if (showArchived) params.archived = "true";
    api.get("/orders", { params }).then((r) => setOrders(r.data));
  };
  useEffect(() => { load(); }, [module, statusFilter, typeFilter, clientFilter, showArchived]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/price-list").then((r) => setPrices(r.data));
  }, []);

  const moduleEmps = employees.filter((e) => !e.specialty || e.specialty === form.module);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const d = form.details;
      const details = Object.fromEntries(Object.entries(d).map(([k, v]) => [k, k === "articles" ? (v || []).filter((a) => (a.qty || 0) > 0) : (k === "mission_type" ? v || null : num(v))]));
      await api.post("/orders", { ...form, employee_id: form.employee_id || null, details });
      toast.success("Bon de commande créé");
      setCreateOpen(false);
      setForm({ client_id: "", module: module !== "tous" ? module : "conciergerie", employee_id: "", scheduled_date: "", description: "", notes: "", supplement_produits: false, origine: "saphir_immo", details: { ...EMPTY_DETAILS } });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitRealize = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${realizeOrder.id}/realize`, {
        started_at: realize.started_at ? new Date(realize.started_at).toISOString() : null,
        ended_at: realize.ended_at ? new Date(realize.ended_at).toISOString() : null,
        materials: realize.materials, receipts: realize.receipts, notes: realize.notes || null,
        details: { ...realizeOrder.details, articles: realize.articles },
      });
      toast.success(`Bon ${realizeOrder.order_number} marqué réalisé`);
      setRealizeOrder(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitInvoice = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/invoices", {
        order_ids: [invoiceOrder.id],
        discount_percent: parseFloat(invForm.discount_percent) || 0,
        line_discounts: { [invoiceOrder.id]: parseFloat(invForm.line_discount) || 0 },
        payment_method: invForm.payment_method || null,
        origine: invForm.origine || null,
      });
      toast.success(`Facture ${r.data.number} créée${r.data.status === "payee" ? " et encaissée (cash)" : ""}`);
      setInvoiceOrder(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const uploadReceipts = async (e) => {
    for (const f of e.target.files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setRealize((prev) => ({ ...prev, receipts: [...prev.receipts, { name: r.data.name, file_id: r.data.id }] }));
        toast.success(`Reçu « ${r.data.name} » téléversé`);
      } catch (err) { toast.error(errMsg(err)); }
    }
    e.target.value = "";
  };

  const refPrice = (() => {
    const a = parseFloat(refForm.amount_ttc);
    const m = parseFloat(refForm.margin_percent);
    if (!(a > 0) || isNaN(m) || m < 0) return null;
    return Math.round(a * (1 + m / 100) * 100) / 100;
  })();

  const openRefModal = () => {
    setRefForm({ client_id: "", label: "", date: new Date().toISOString().slice(0, 10), amount_ttc: "", margin_percent: "0", receipt: null });
    setRefOpen(true);
  };

  const submitRefacturation = async (e) => {
    e.preventDefault();
    try {
      await api.post("/refacturations", {
        client_id: refForm.client_id, label: refForm.label, date: refForm.date || null,
        amount_ttc: parseFloat(refForm.amount_ttc), margin_percent: parseFloat(refForm.margin_percent) || 0,
        receipt: refForm.receipt,
      });
      toast.success("Refacturation créée (réalisée, prête à facturer)");
      setRefOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const uploadRefReceipt = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefForm((p) => ({ ...p, receipt: { name: r.data.name, file_id: r.data.id } }));
      toast.success(`Reçu « ${r.data.name} » téléversé`);
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  const openReceipt = async (fileId, name) => {
    try {
      const r = await api.get(`/files/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "recu";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openArchiveModal = async () => {
    try {
      const r = await api.get("/orders/archivable-count");
      setArchiveInfo({ count: r.data.count });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const confirmArchive = async () => {
    try {
      const r = await api.post("/orders/archive-invoiced");
      toast.success(`${r.data.archived} bon(s) facturé(s) archivé(s)`);
      setArchiveInfo(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer le bon ${o.order_number} ?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Bon supprimé"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const d = editOrder.details;
      const details = Object.fromEntries(Object.entries(d).map(([k, v]) => [k, k === "articles" ? (v || []).filter((a) => (a.qty || 0) > 0) : (k === "mission_type" ? v || null : num(v))]));
      await api.put(`/orders/${editOrder.id}`, { details, description: editOrder.description, scheduled_date: editOrder.scheduled_date, notes: editOrder.notes, supplement_produits: !!editOrder.supplement_produits, origine: editOrder.origine || "saphir_immo" });
      toast.success("Bon modifié (facture recalculée si déjà émise)");
      setEditOrder(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const detailFields = (m, details, setDetails, prefix) => {
    const plist = prices.filter((p) => p.module === m);
    return (
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 border border-slate-200 bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`${prefix}-articles`}>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Prestations — liste des prix {MODULES[m].label}</label>
          <ArticleRows articles={details.articles || []} setArticles={(arts) => setDetails({ ...details, articles: arts })} prefix={prefix} />
          <ArticleAdder articles={details.articles || []} setArticles={(arts) => setDetails({ ...details, articles: arts })} prices={plist} prefix={prefix} />
          {(details.articles || []).length > 0 && (
            <div className="text-xs text-slate-700 text-right font-medium" data-testid={`${prefix}-articles-total`}>
              Total : {fmtCHF(details.articles.reduce((s, a) => s + (a.qty || 0) * (a.unit_price || 0), 0))}
            </div>
          )}
          {plist.length === 0 && <div className="text-xs text-amber-700">Aucun article dans la liste des prix « {MODULES[m].label} » — créez-la dans « Liste des prix ».</div>}
        </div>
        <Field label="Prix convenu (CHF, optionnel)" testid={`${prefix}-override`} value={details.price_override} onChange={(v) => setDetails({ ...details, price_override: v })} type="number" />
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Bons de commande</h1>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <button data-testid="archive-invoiced-button" onClick={openArchiveModal}
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-100 transition-colors">
              <Archive size={16} /> Archiver les bons facturés
            </button>
          )}
          {user?.role !== "employe" && (
            <button data-testid="create-refacturation-button" onClick={openRefModal}
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-100 transition-colors">
              <ReceiptText size={16} /> Nouvelle refacturation
            </button>
          )}
          <button data-testid="create-order-slip-button" onClick={() => { setForm({ ...form, module: module !== "tous" ? module : "conciergerie" }); setCreateOpen(true); }}
            className="flex items-center gap-2 bg-slate-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors">
            <Plus size={16} /> Nouveau bon
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="order-status-filters">
        {[["", "Tous"], ...Object.entries(ORDER_STATUS).map(([k, v]) => [k, v.label])].map(([k, label]) => (
          <button key={k} data-testid={`order-filter-${k || "all"}`} onClick={() => setStatusFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === k ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
            {label}
          </button>
        ))}
        <select data-testid="order-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:bg-slate-100">
          <option value="">Tous types</option>
          <option value="standard">Bons standard</option>
          <option value="refacturation">Refacturations</option>
        </select>
        <ClientCombobox testid="order-filter-client" clients={clients} value={clientFilter} onChange={setClientFilter} />
        <button data-testid="order-filter-archived" onClick={() => setShowArchived((v) => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${showArchived ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
          Afficher les archivés
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <th className="px-5 py-3">N° / Module</th><th className="px-5 py-3">Client</th><th className="px-5 py-3">Détail</th>
              <th className="px-5 py-3">Employé</th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Montant HT</th>
              <th className="px-5 py-3">Statut</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="orders-table">
            {orders.map((o) => {
              const isRef = o.order_type === "refacturation";
              const M = MODULES[o.module];
              const st = ORDER_STATUS[o.status];
              return (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <div className="font-mono2 text-xs font-semibold text-slate-900">{o.order_number}</div>
                    {isRef
                      ? <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ORDER_TYPE.refacturation.cls}`} data-testid={`order-type-badge-${o.id}`}>{ORDER_TYPE.refacturation.label}</span>
                      : <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${M.badge}`}>{M.label}</span>}
                    <span className={`ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${ORIGINES[o.origine || "saphir_immo"].cls}`}>{ORIGINES[o.origine || "saphir_immo"].label}</span>
                    {o.archived && <span className="ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-500" data-testid={`order-archived-badge-${o.id}`}>archivé</span>}
                    {o.supplement_produits && <div className="text-[11px] text-amber-700 font-semibold mt-0.5">+10 % produits Saphir</div>}
                  </td>
                  <td className="px-5 py-3 text-slate-900 font-medium">{clientMap[o.client_id] ? clientName(clientMap[o.client_id]) : "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{orderSummary(o)}</td>
                  <td className="px-5 py-3 text-slate-600">{isRef ? "—" : (empMap[o.employee_id]?.name || "Non attribué")}</td>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{fmtDate(o.scheduled_date)}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900 whitespace-nowrap">{o.amounts ? fmtCHF(o.amounts.total_ht) : "—"}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid={`order-status-${o.id}`}>{st.label}</span></td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {isRef && user?.role !== "employe" && (
                      <button data-testid={`view-refacturation-${o.id}`} title="Détail refacturation" onClick={() => setRefDetail(o)}
                        className="text-slate-400 hover:text-slate-900 p-1"><Eye size={16} /></button>
                    )}
                    {!isRef && ["planifie", "en_cours"].includes(o.status) && (
                      <button data-testid={`realize-order-${o.id}`} title="Réaliser" onClick={() => { setRealize({ started_at: "", ended_at: "", materials: [], receipts: [], notes: "", articles: o.details?.articles || [] }); setRealizeOrder(o); }}
                        className="text-amber-500 hover:text-amber-700 p-1"><CheckCircle2 size={16} /></button>
                    )}
                    {o.status === "realise" && !o.invoice_id && (
                      <button data-testid={`invoice-order-${o.id}`} title="Facturer" onClick={() => { setInvForm({ discount_percent: 0, payment_method: "" }); setInvoiceOrder(o); }}
                        className="text-emerald-600 hover:text-emerald-800 p-1"><FileText size={16} /></button>
                    )}
                    {!isRef && (
                      <button data-testid={`edit-order-${o.id}`} title="Modifier" onClick={() => setEditOrder({ ...o, details: { ...EMPTY_DETAILS, ...o.details } })}
                        className="text-slate-400 hover:text-slate-900 p-1"><Pencil size={15} /></button>
                    )}
                    {!o.invoice_id && (
                      <button data-testid={`delete-order-${o.id}`} title="Supprimer" onClick={() => remove(o)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {orders.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Aucun bon de commande.</td></tr>}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="create-order-slip-modal">
          <form onSubmit={submitCreate} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Nouveau bon de commande</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client *</label>
                <select data-testid="order-form-client" required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option value="">— Choisir —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Module *</label>
                <select data-testid="order-form-module" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value, employee_id: "" })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  {Object.entries(MODULES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Employé</label>
                <select data-testid="order-form-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option value="">— Non attribué —</option>
                  {moduleEmps.map((e2) => <option key={e2.id} value={e2.id}>{e2.name}{e2.specialty ? ` (${MODULES[e2.specialty]?.label})` : ""}</option>)}
                </select>
              </div>
              <DateField label="Date prévue *" testid="order-form-date" required value={form.scheduled_date} onChange={(v) => setForm({ ...form, scheduled_date: v })} />
            </div>
            {detailFields(form.module, form.details, (d) => setForm({ ...form, details: d }), "order-form")}
            <Field label="Description" testid="order-form-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
            <Field label="Notes" testid="order-form-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="order-form-supplement" type="checkbox" checked={form.supplement_produits} onChange={(e) => setForm({ ...form, supplement_produits: e.target.checked })} />
              Supplément 10 % pour utilisation des produits de Saphir Immo SA
            </label>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Origine du client</label>
              <select data-testid="order-form-origine" value={form.origine} onChange={(e) => setForm({ ...form, origine: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="saphir_immo">Client Saphir Immo SA</option>
                <option value="service_location">Client du service location</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="order-form-cancel" onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="order-form-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Créer le bon</button>
            </div>
          </form>
        </div>
      )}

      {editOrder && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="edit-order-modal">
          <form onSubmit={saveEdit} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Modifier {editOrder.order_number}</h2>
            {editOrder.invoice_id && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2" data-testid="edit-order-invoiced-warning">Ce bon est déjà facturé : la facture sera automatiquement recalculée.</div>}
            <DateField label="Date prévue" testid="edit-order-date" value={editOrder.scheduled_date} onChange={(v) => setEditOrder({ ...editOrder, scheduled_date: v })} />
            {detailFields(editOrder.module, editOrder.details, (d) => setEditOrder({ ...editOrder, details: d }), "edit-order")}
            <Field label="Description" testid="edit-order-description" value={editOrder.description || ""} onChange={(v) => setEditOrder({ ...editOrder, description: v })} />
            <Field label="Notes" testid="edit-order-notes" value={editOrder.notes || ""} onChange={(v) => setEditOrder({ ...editOrder, notes: v })} />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="edit-order-supplement" type="checkbox" checked={!!editOrder.supplement_produits} onChange={(e) => setEditOrder({ ...editOrder, supplement_produits: e.target.checked })} />
              Supplément 10 % pour utilisation des produits de Saphir Immo SA
            </label>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Origine du client</label>
              <select data-testid="edit-order-origine" value={editOrder.origine || "saphir_immo"} onChange={(e) => setEditOrder({ ...editOrder, origine: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="saphir_immo">Client Saphir Immo SA</option>
                <option value="service_location">Client du service location</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="edit-order-cancel" onClick={() => setEditOrder(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="edit-order-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Enregistrer</button>
            </div>
          </form>
        </div>
      )}

      {realizeOrder && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="realize-order-modal">
          <form onSubmit={submitRealize} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Réaliser {realizeOrder.order_number}</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Heure d'arrivée (facultatif)" testid="realize-started" type="datetime-local" value={realize.started_at} onChange={(v) => setRealize({ ...realize, started_at: v })} />
              <Field label="Heure de départ (facultatif)" testid="realize-ended" type="datetime-local" value={realize.ended_at} onChange={(v) => setRealize({ ...realize, ended_at: v })} />
            </div>
            <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 space-y-2" data-testid="realize-articles">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Prestations {MODULES[realizeOrder.module].label} — quantités réelles</label>
              <ArticleRows articles={realize.articles} setArticles={(arts) => setRealize({ ...realize, articles: arts })} prefix="realize" />
              <ArticleAdder articles={realize.articles} setArticles={(arts) => setRealize({ ...realize, articles: arts })} prices={prices.filter((p) => p.module === realizeOrder.module)} prefix="realize" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Matériel acheté (montants TTC)</label>
              <div className="flex gap-2 mt-1">
                <input data-testid="realize-material-name" placeholder="Produit" value={mat.name} onChange={(e) => setMat({ ...mat, name: e.target.value })}
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <input data-testid="realize-material-price" placeholder="CHF TTC" type="number" step="0.05" value={mat.price} onChange={(e) => setMat({ ...mat, price: e.target.value })}
                  className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button type="button" data-testid="realize-material-add"
                  onClick={() => { if (mat.name && mat.price) { setRealize({ ...realize, materials: [...realize.materials, { name: mat.name, price: parseFloat(mat.price) }] }); setMat({ name: "", price: "" }); } }}
                  className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200"><Plus size={15} /></button>
              </div>
              <div className="mt-2 space-y-1" data-testid="realize-materials-list">
                {realize.materials.map((m, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                    <span>{m.name}</span>
                    <span className="font-semibold">{fmtCHF(m.price)} TTC</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reçus (photos / PDF)</label>
              <input data-testid="realize-receipts" type="file" multiple accept="image/*,application/pdf" onChange={uploadReceipts}
                className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:px-3 file:py-1.5 file:text-xs file:font-semibold" />
              <div className="mt-2 space-y-1" data-testid="realize-receipts-list">
                {realize.receipts.map((r, i) => (
                  <div key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700">{r.name}</div>
                ))}
              </div>
            </div>
            <Field label="Notes" testid="realize-notes" value={realize.notes} onChange={(v) => setRealize({ ...realize, notes: v })} />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="realize-cancel" onClick={() => setRealizeOrder(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="realize-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600">Marquer réalisé</button>
            </div>
          </form>
        </div>
      )}

      {invoiceOrder && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="invoice-order-modal">
          <form onSubmit={submitInvoice} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Contrôle — {invoiceOrder.order_number}</h2>
            <div className="text-sm text-slate-600">Client : <b>{clientName(clientMap[invoiceOrder.client_id] || {})}</b></div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5 text-sm" data-testid="invoice-control-summary">
              <div className="flex justify-between"><span className="text-slate-600">Prestation ({orderSummary(invoiceOrder)})</span><span className="font-medium">{fmtCHF(invoiceOrder.amounts?.prestation)}</span></div>
              {invoiceOrder.supplement_produits && <div className="text-xs text-amber-700">Supplément 10 % produits Saphir Immo SA inclus</div>}
              {(invoiceOrder.materials || []).map((m, i) => (
                <div key={i} className="flex justify-between text-slate-500"><span>Matériel : {m.name}</span><span>{fmtCHF(m.price)} TTC</span></div>
              ))}
              <div className="flex justify-between border-t border-slate-200 pt-1.5 font-semibold text-slate-900"><span>Total HT</span><span>{fmtCHF(invoiceOrder.amounts?.total_ht)}</span></div>
            </div>
            {(clientMap[invoiceOrder.client_id]?.discounts?.[invoiceOrder.module] || 0) > 0 && (
              <div className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2" data-testid="invoice-client-discount-info">
                Rabais client automatique ({MODULES[invoiceOrder.module].label}) : {clientMap[invoiceOrder.client_id].discounts[invoiceOrder.module]} %
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Rabais sur cette prestation (%)" testid="invoice-line-discount" type="number" value={invForm.line_discount} onChange={(v) => setInvForm({ ...invForm, line_discount: v })} />
              <Field label="Rabais sur la facture (%)" testid="invoice-discount" type="number" value={invForm.discount_percent} onChange={(v) => setInvForm({ ...invForm, discount_percent: v })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Paiement</label>
              <select data-testid="invoice-payment-method" value={invForm.payment_method} onChange={(e) => setInvForm({ ...invForm, payment_method: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="">Facture à payer (30 j)</option>
                <option value="cash">Cash — payé sur le moment</option>
                <option value="virement">Virement</option>
                <option value="carte">Carte</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Origine de facturation</label>
              <select data-testid="invoice-origine" value={invForm.origine} onChange={(e) => setInvForm({ ...invForm, origine: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="">— Comme le bon ({ORIGINES[invoiceOrder.origine || "saphir_immo"].label}) —</option>
                <option value="saphir_immo">Client Saphir Immo SA</option>
                <option value="service_location">Client du service location</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="invoice-cancel" onClick={() => setInvoiceOrder(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="invoice-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">Valider le bon et créer la facture</button>
            </div>
          </form>
        </div>
      )}

      {archiveInfo && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="archive-modal">
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Archiver les bons facturés</h2>
            {archiveInfo.count === 0 ? (
              <p className="text-sm text-slate-600" data-testid="archive-modal-empty">Aucun bon facturé à archiver.</p>
            ) : (
              <p className="text-sm text-slate-600" data-testid="archive-modal-count">
                <b>{archiveInfo.count}</b> bon(s) facturé(s) seront archivés. Ils disparaîtront de la liste par défaut (bascule « Afficher les archivés » pour les revoir). Les factures, PDF et chiffres ne sont pas affectés.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="archive-cancel" onClick={() => setArchiveInfo(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">{archiveInfo.count === 0 ? "Fermer" : "Annuler"}</button>
              {archiveInfo.count > 0 && (
                <button data-testid="archive-confirm" onClick={confirmArchive}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Archiver</button>
              )}
            </div>
          </div>
        </div>
      )}

      {refOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="refacturation-modal">
          <form onSubmit={submitRefacturation} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Nouvelle refacturation</h2>
            <p className="text-xs text-slate-500">Refacture au client un article acheté pour lui. Créée directement au statut « Réalisé », prête à facturer.</p>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client *</label>
              <select data-testid="refacturation-form-client" required value={refForm.client_id} onChange={(e) => setRefForm({ ...refForm, client_id: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="">— Choisir —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
              </select>
            </div>
            <Field label="Libellé (texte sur la facture) *" testid="refacturation-form-label" required value={refForm.label} onChange={(v) => setRefForm({ ...refForm, label: v })} />
            <DateField label="Date *" testid="refacturation-form-date" required value={refForm.date} onChange={(v) => setRefForm({ ...refForm, date: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Montant du reçu TTC (CHF) *" testid="refacturation-form-amount" type="number" step="0.01" required value={refForm.amount_ttc} onChange={(v) => setRefForm({ ...refForm, amount_ttc: v })} />
              <Field label="Marge (%) *" testid="refacturation-form-margin" type="number" required value={refForm.margin_percent} onChange={(v) => setRefForm({ ...refForm, margin_percent: v })} />
            </div>
            {refPrice !== null && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm flex justify-between" data-testid="refacturation-computed-price">
                <span className="text-slate-600">Prix facturé TTC (calculé)</span>
                <span className="font-semibold text-slate-900">{fmtCHF(refPrice)}</span>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Photo du reçu (facultatif)</label>
              <input data-testid="refacturation-form-receipt" type="file" accept="image/*,application/pdf" onChange={uploadRefReceipt}
                className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:px-3 file:py-1.5 file:text-xs file:font-semibold" />
              {refForm.receipt && (
                <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700" data-testid="refacturation-receipt-name">{refForm.receipt.name}</div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="refacturation-cancel" onClick={() => setRefOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="refacturation-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Créer la refacturation</button>
            </div>
          </form>
        </div>
      )}

      {refDetail && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="refacturation-detail-modal">
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-3">
            <h2 className="text-base font-semibold text-slate-900">Refacturation {refDetail.order_number}</h2>
            <div className="text-sm space-y-1.5" data-testid="refacturation-detail-body">
              <DetailRow label="Client" value={clientMap[refDetail.client_id] ? clientName(clientMap[refDetail.client_id]) : "—"} />
              <DetailRow label="Libellé (sur la facture)" value={refDetail.ref_label || refDetail.description} />
              <DetailRow label="Date" value={fmtDate(refDetail.scheduled_date)} />
              <DetailRow label="Montant du reçu TTC" value={fmtCHF(refDetail.purchase_amount_ttc)} />
              <DetailRow label="Marge" value={`${refDetail.margin_percent ?? 0} %`} />
              <DetailRow label="Prix facturé TTC" value={fmtCHF(refDetail.price_ttc)} bold />
              <DetailRow label="Statut" value={ORDER_STATUS[refDetail.status]?.label || refDetail.status} />
            </div>
            {(refDetail.receipts || []).length > 0 && (
              <button data-testid="refacturation-detail-receipt" onClick={() => openReceipt(refDetail.receipts[0].file_id, refDetail.receipts[0].name)}
                className="text-sm text-sky-700 hover:text-sky-900 underline">
                Reçu : {refDetail.receipts[0].name}
              </button>
            )}
            <div className="flex justify-end pt-2">
              <button data-testid="refacturation-detail-close" onClick={() => setRefDetail(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, bold = false }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className={bold ? "font-semibold text-slate-900 text-right" : "text-slate-700 text-right"}>{value}</span>
    </div>
  );
}

function DateField({ label, testid, value, onChange, required = false }) {
  const toCh = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10).split("-").reverse().join(".") : "");
  const [text, setText] = useState(toCh(value));
  useEffect(() => { setText(toCh(value)); }, [value]);
  const invalid = !!text && !/^\d{2}\.\d{2}\.\d{4}$/.test(text);
  const handle = (v) => {
    setText(v);
    const m = v.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) onChange(`${m[3]}-${m[2]}-${m[1]}`);
  };
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <input data-testid={testid} type="text" inputMode="numeric" placeholder="JJ.MM.AAAA" required={required}
        value={text} onChange={(e) => handle(e.target.value)}
        pattern="\d{2}\.\d{2}\.\d{4}" title="Format JJ.MM.AAAA"
        className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none ${invalid ? "border-red-400 bg-red-50" : "border-slate-300"}`} />
      {invalid && <p className="text-[11px] text-red-600 mt-0.5">Format attendu : JJ.MM.AAAA</p>}
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required = false, step }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <input data-testid={testid} type={type} required={required} step={type === "number" ? (step || "0.05") : undefined}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none" />
    </div>
  );
}
